import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray } from "../common/utils/arrayUtilities";
import { dataHookFactory } from "../common/reducers/utilities";
import { logout } from "../common/reducers/initContext";

const compareSitrepDefs = (d1, d2) => d1.id === d2.id;

const sitrepDefSlice = createSlice({
    name: 'sitrepDef',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                const idx = state.data.findIndex(s => compareSitrepDefs(s, def));
                if (idx >= 0) {
                    state.data.splice(idx, 1, def);
                } else {
                    state.data.push(def);
                }
            }
        },
        remove: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                const idx = state.data.findIndex(s => compareSitrepDefs(s, def));
                if (idx > -1) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => sitrepDefSlice.getInitialState());
    }
});

// #region network operations
const fetchSitrepDefs = network => network.get('/api/v1/sitrepDefs', sitrepDefSlice.actions.set);
export const createSitrepDef = (network, def) => network.post('/api/v1/sitrepDef', def, sitrepDefSlice.actions.addOrUpdate);
export const updateSitrepDef = (network, def) => network.put('/api/v1/sitrepDef', def, sitrepDefSlice.actions.addOrUpdate);
export const removeSitrepDef = (network, def) => network.delete('/api/v1/sitrepDef/' + encodeURIComponent(def.id), sitrepDefSlice.actions.remove);
// #endregion

// #region permission checks
export const canCreateDef = (credentials, def) => credentials.access.hasFull('administrator');
export const canUpdateDef = (credentials, def) => credentials.access.hasFull('administrator');
export const canRemoveDef = (credentials, def) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectSitrepDefs = state => state.sitrepDef.data;

const selectSitrepDefById = createSelector([selectSitrepDefs, (state, id) => id], (sitreps, id) => sitreps.find(s => s.id === id));
const selectSitrepDef = id => state => selectSitrepDefById(state, id);
// #endregion

// #region listners
const listen = {
    sitrepDefCreated: (def, { dispatch }) => dispatch(sitrepDefSlice.actions.addOrUpdate(def)),
    sitrepDefUpdated: (def, { dispatch }) => dispatch(sitrepDefSlice.actions.addOrUpdate(def)),
    sitrepDefRemoved: (def, { dispatch }) => dispatch(sitrepDefSlice.actions.remove(def))
}
// #endregion

const init = { run: fetchSitrepDefs, listen };
export const [useSitrepDefs] = dataHookFactory(selectSitrepDefs, init);
export const [useSitrepDef] = dataHookFactory(selectSitrepDef, id => init);

const reducerDetails = { name: sitrepDefSlice.name, reducer: sitrepDefSlice.reducer };
export default reducerDetails;